import React, { useEffect, useMemo, useState } from "react";

import { ContentSection } from "../Layout/ContentSection";
import { api } from "../api";
import { API_Compo } from "../../../src/apidefs";
import { FeedItemUpcomingCompo, FeedItemEvent } from "../../../server/feed/buildfeed";
import { assertNever, Util } from "../util";
import { RLink } from "../DesignSystem/RLink";

type CalendarProps = {
  compos: API_Compo;
};

type CalendarEvent = {
  type: "event" | "compo";
  length: number;
  start: Date;
  title: string;
  url?: string;
  shortTitle: string;
};

const calendarDateBoxClasses =
  "relative -mt-[2px] -ml-[2px] h-[100px] flex-1 border-2 border-[#eee] bg-white";
const calendarRowClasses = "flex";
const calendarWeekdayClasses = "flex-1 bg-white h-[25px]";
const calendarEventBaseClasses = "relative mt-[5px] h-[20px] font-medium text-[#fd8689]";
const calendarTextClasses = "absolute z-10 whitespace-nowrap overflow-visible text-black";
const calendarDateNumberClasses = "p-[5px] text-right text-[#ccc]";
const calendarTodayClasses = "border-2 border-[#efa6a6]";
const tooltipClasses =
  "absolute top-[25px] z-10 block w-[300px] rounded border border-[#dad5d5] bg-white p-[3px] text-[14px] text-black";
const EVENT_COLORS = ["bg-[rgb(162,45,45)]", "bg-[rgb(96,179,56)]", "bg-[rgb(56,87,179)]"];

const CompoCalendar = ({ compos }: CalendarProps): JSX.Element => {
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [highlightedEvent, setHighlightedEvent] = useState<string>();

  useEffect(() => {
    let cancelled = false;

    const loadEvents = async () => {
      const feed = await api.get("/api/feed");
      const composAndEvents = feed.filter(
        (item) => item.type === "upcoming-contest" || item.type === "event"
      ) as (FeedItemUpcomingCompo | FeedItemEvent)[];

      const calendarEvents: CalendarEvent[] = [];

      for (const item of composAndEvents) {
        if (item.type === "upcoming-contest") {
          const entryPeriodDiff =
            new Date(item.compo.stop).getTime() - new Date(item.compo.start).getTime();
          const judgePeriodDiff =
            new Date(item.compo.judging_stop).getTime() - new Date(item.compo.stop).getTime();

          calendarEvents.push({
            type: "compo",
            length: Math.round(entryPeriodDiff / (1000 * 60 * 60 * 24)),
            start: new Date(item.compo.start),
            url: `/compo/${item.compo.id}`,
            title: item.compo.name,
            shortTitle: item.compo.name,
          });

          calendarEvents.push({
            type: "compo",
            length: Math.round(judgePeriodDiff / (1000 * 60 * 60 * 24)),
            start: new Date(item.compo.stop),
            url: `/compo/${item.compo.id}`,
            title: `${item.compo.name} judging`,
            shortTitle: `${item.compo.name} judging`,
          });
        } else if (item.type === "event") {
          const length = 1;
          const title = item.title;
          calendarEvents.push({
            type: "event",
            length,
            start: new Date(item.start),
            title,
            shortTitle:
              length === 1 && title.toLowerCase().includes("sync") ? "Sync" : item.title,
          });
        } else {
          assertNever(item);
          return;
        }
      }

      if (!cancelled) {
        setEvents(calendarEvents);
      }
    };

    loadEvents();

    return () => {
      cancelled = true;
    };
  }, [compos]);

  const now = new Date();
  const month = now.getMonth();
  const monthName = now.toLocaleString("en-us", { month: "long" });
  const year = now.getFullYear();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const { eventsPerDay } = useMemo(() => {
    const heights: Record<string, number> = {};
    const perDay: (CalendarEvent & { firstDay: boolean })[][] = Array.from(
      { length: daysInMonth + 1 },
      () => []
    );

    for (let dayOfMonth = 1; dayOfMonth < daysInMonth; dayOfMonth++) {
      const day = new Date(year, month, dayOfMonth);

      const todaysEvents = events.filter((ev) => Util.IsDayWithinRange(day, ev.start, ev.length));
      const todaysEventsThatStartedToday = events.filter(
        (ev) =>
          dayOfMonth === 1 ||
          (ev.start.getDate() === day.getDate() && ev.start.getMonth() === day.getMonth())
      );

      for (const ev of todaysEventsThatStartedToday) {
        for (let height = 0; ; height++) {
          if (todaysEvents.filter((event) => heights[JSON.stringify(event)] === height).length === 0) {
            heights[JSON.stringify(ev)] = height;
            break;
          }
        }
      }

      for (const ev of todaysEvents) {
        const eventHeight = heights[JSON.stringify(ev)];
        perDay[dayOfMonth][eventHeight] = {
          ...ev,
          firstDay:
            dayOfMonth === 1 ||
            (ev.start.getDate() === day.getDate() && ev.start.getMonth() === day.getMonth()),
        };
      }
    }

    return { eventsPerDay: perDay };
  }, [events, daysInMonth, month, year]);

  if (events.length === 0) {
    return <div> Loading! </div>;
  }

  const calendarRows: JSX.Element[] = [];
  for (let weekStartDay = 1; weekStartDay <= daysInMonth; weekStartDay++) {
    const calendarDays: JSX.Element[] = [];
    let day: number;
    const lastDayOfWeek = weekStartDay + 7;

    for (day = weekStartDay; day < lastDayOfWeek; day++) {
      if (day > daysInMonth) {
        calendarDays.push(<div key={`empty-${day}`} className={calendarDateBoxClasses}></div>);
        continue;
      }

      const todaysEvents = eventsPerDay[day];
      const eventJSX: JSX.Element[] = [];

      for (let i = 0; i < todaysEvents.length; i++) {
        const ev = eventsPerDay[day][i];
        if (!ev) {
          continue;
        }

        const isHighlighted = highlightedEvent === ev.title;

        eventJSX.push(
          <div
            key={`${ev.title}-${i}`}
            onMouseOver={() => setHighlightedEvent(ev.title)}
            onMouseOut={() => setHighlightedEvent(undefined)}
            className={`${calendarEventBaseClasses} ${EVENT_COLORS[i % EVENT_COLORS.length]} ${
              isHighlighted ? "text-white" : ""
            }`}
            style={{
              width: "calc(100% + 4px)",
              top: `${(i + 1) * 25}px`,
              marginTop: "-20px",
            }}
          >
            <div
              className={`${calendarTextClasses} ${
                isHighlighted ? "text-white" : "text-black"
              }`}
            >
              {ev.url ? (
                <RLink white to={ev.url}>
                  {ev.firstDay ? ev.shortTitle : ""}
                </RLink>
              ) : ev.firstDay ? (
                ev.shortTitle
              ) : (
                ""
              )}
            </div>

            {isHighlighted && ev.firstDay && (
              <div
                onMouseOver={() => setHighlightedEvent(ev.title)}
                onMouseOut={() => setHighlightedEvent(undefined)}
                className={tooltipClasses}
              >
                <div className="pb-[10px] text-[18px]">{ev.title}</div>
                <div>Starts on: {Util.FormatDate(ev.start)}</div>
              </div>
            )}
          </div>
        );
      }

      calendarDays.push(
        <div
          key={day}
          className={`${calendarDateBoxClasses} ${
            day === now.getDate() ? calendarTodayClasses : ""
          }`}
        >
          <div className={calendarDateNumberClasses}>{day}</div>
          {eventJSX}
        </div>
      );
    }

    calendarRows.push(
      <div key={weekStartDay} className={calendarRowClasses}>
        {calendarDays}
      </div>
    );
    weekStartDay = day - 1;
  }

  return (
    <div>
      <h3>{monthName}</h3>

      <div className={calendarRowClasses}>
        {[1, 2, 3, 4, 5, 6, 7].map((dayIndex) => (
          <div key={dayIndex} className={calendarWeekdayClasses}>
            {new Date(year, month, dayIndex).toLocaleDateString("en-us", {
              weekday: "long",
            })}
          </div>
        ))}
      </div>

      {calendarRows}
    </div>
  );
};

export const FutureComposPage = (): JSX.Element => {
  const [compos, setCompos] = useState<API_Compo>();

  useEffect(() => {
    let cancelled = false;

    const loadCompos = async () => {
      const results = await api.get("/api/compo");
      if (!cancelled) {
        setCompos(results);
      }
    };

    loadCompos();

    return () => {
      cancelled = true;
    };
  }, []);

  if (!compos) {
    return (
      <div>
        <ContentSection>
          <h2> Loading </h2>
        </ContentSection>
      </div>
    );
  }

  return (
    <div>
      <ContentSection>
        <h2> Calendar </h2>
        <CompoCalendar compos={compos} />
      </ContentSection>
    </div>
  );
};
